/**
 * Live casting state.
 *
 * Mutable and read every frame by the HUD's animation loop and the 3D scene;
 * only the coarse booleans are mirrored into React, and only when they flip.
 */
import { create } from 'zustand';

export interface CastState {
  active: boolean;
  /** Epoch-aligned moment the cast key went down. */
  startedAt: number;
  /** Epoch-aligned speech onset, from the microphone. 0 until detected. */
  onsetAt: number;
  /** Best transcript so far (interim or final). */
  transcript: string;
  /** True once the recogniser has produced a final result this cast. */
  finalized: boolean;
  /**
   * The client's live *guess* at which spell is being performed, scored locally
   * against the player's own templates as the sigil is drawn.
   *
   * Cosmetic, and only ever cosmetic: it colours the magic circle and
   * highlights a row in the HUD so the player can see what they are about to
   * cast. The server identifies the spell again from the same evidence and its
   * answer is the only one that counts — a wrong guess here changes what the
   * screen looks like mid-cast and nothing else.
   */
  guessId: string;
  /** 0..1 confidence in `guessId`; drives how committed the visuals look. */
  guessScore: number;
  color: string;
  /** 0..1 estimated progress through the guessed spell's chant. */
  progress: number;
}

export const castState: CastState = {
  active: false,
  startedAt: 0,
  onsetAt: 0,
  transcript: '',
  finalized: false,
  guessId: '',
  guessScore: 0,
  color: '#8fd8ff',
  progress: 0,
};

/**
 * What the player actually said on the most recent cast, kept past `resetCast`
 * because the verdict arrives from the server after the cast state is gone.
 *
 * Without it a failed cast is a dead end — "영창을 알아보지 못했습니다" never
 * tells the player whether the microphone dropped a word or they said the wrong
 * one, so there is nothing to correct next time.
 */
export const lastAttempt = { heard: '', expected: '' };

export function resetCast() {
  castState.active = false;
  castState.startedAt = 0;
  castState.onsetAt = 0;
  castState.transcript = '';
  castState.finalized = false;
  castState.guessId = '';
  castState.guessScore = 0;
  castState.progress = 0;
}

/* ------------------------------------------------------------------ */

export interface CastUiState {
  /** Mirrors castState.active, for components that must re-render on it. */
  casting: boolean;
  /** The keyboard chant used when speech is unavailable. */
  typedChant: string;
  usingFallbackInput: boolean;
  /** Latest judgement, shown as a floating grade. */
  lastResult: {
    grade: string;
    gesture: number;
    chant: number;
    reason: string | null;
    parry: boolean;
    timingError: number;
    at: number;
    /** Transcript and registered chant, so a failure can be read side by side. */
    heard: string;
    expected: string;
  } | null;

  setCasting: (casting: boolean) => void;
  setTypedChant: (text: string) => void;
  setFallback: (on: boolean) => void;
  setLastResult: (result: CastUiState['lastResult']) => void;
}

export const useCastUi = create<CastUiState>((set) => ({
  casting: false,
  typedChant: '',
  usingFallbackInput: false,
  lastResult: null,
  setCasting: (casting) => set({ casting }),
  setTypedChant: (typedChant) => set({ typedChant: typedChant.slice(0, 48) }),
  setFallback: (usingFallbackInput) => set({ usingFallbackInput }),
  setLastResult: (lastResult) => set({ lastResult }),
}));
